import { useQuery } from '@tanstack/react-query';
import { useParams, Navigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { api } from '@/lib/api';
import type { Project as ProjectType, TestCase as TestCaseType } from '@/lib/types';
import { TestRunner } from '@/components/test-runner';

export default function ProjectTestsPage(): import('react/jsx-runtime').JSX.Element {
  const { id = '' } = useParams<{ id: string }>();

  const { data: projectRes, isLoading: projectLoading } = useQuery({
    queryKey: ['project', id],
    queryFn: () => api.getProject(id),
    enabled: id.length > 0,
    staleTime: 30 * 1000,
  });

  const { data: testCasesRes, isLoading: testsLoading } = useQuery({
    queryKey: ['test-cases', id],
    queryFn: () => api.getTestCases(id),
    enabled: id.length > 0,
    staleTime: 30 * 1000,
  });

  if (projectLoading || testsLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted border-t-foreground" />
      </div>
    );
  }

  const p = projectRes?.project;
  if (p?.id == null) return <Navigate to="/projects" replace />;

  const project: ProjectType = {
    id: p.id,
    name: p.name ?? '',
    owner_id: '' as string,
    org_id: p.organization?.id ?? null,
    secret: '' as string,
    agent_url: p.agentUrl ?? null,
    created_at: new Date(0).toISOString(),
    deleted_at: null,
  };

  const testCases: TestCaseType[] = (testCasesRes?.testCases ?? []).map((row) => ({
    id: row.id ?? '',
    project_id: row.projectId ?? '',
    execution_id: row.executionId ?? '',
    name: row.name ?? '',
    entry: row.entry ?? null,
    input_hash: null,
    output_hash: null,
    input_snapshot: null,
    output_snapshot: null,
    last_status: (row.lastStatus ?? 'pending') as TestCaseType['last_status'],
    last_run_at: row.lastRunAt ?? null,
    created_by: null,
    created_at: new Date(0).toISOString(),
  }));

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 space-y-6">
      <div className="flex items-center gap-3">
        <Link
          to={`/projects/${id}`}
          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-white/[0.06] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="text-lg font-semibold">{project.name} — Tests</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Pinned executions that are replayed to verify function behaviour.
          </p>
        </div>
      </div>

      <TestRunner projectId={id} agentUrl={project.agent_url} initialTestCases={testCases} />
    </div>
  );
}
